import React, { useCallback, useEffect, useImperativeHandle } from "react";
import { message, Select } from "antd";
import ShippingAddressDTO from "@/dto/ShippingAddressDTO";
import waybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import { useGlobalStore } from "@/store/hooks";
import { observer } from "mobx-react-lite";
import { RedoOutlined } from "@ant-design/icons";

/**
 * 获取默认发货人地址信息
 */
function getDefaultShippingAddress(
    shippingAddresses: ShippingAddressDTO[],
    waybillTemplate?: WaybillTemplateDTO,
    shopId?: number,
    defaultShippingAddressId?: number,
) {
    /** 手工订单默认发货人 */
    if (defaultShippingAddressId) {
        const find = shippingAddresses.find((it) => it.id === defaultShippingAddressId);

        if (find) return find;
    }

    // 电子面单默认绑定发货地址
    if (waybillTemplate && waybillTemplate.defaultShippingAddressId) {
        const find = shippingAddresses.find((it) => it.id === waybillTemplate.defaultShippingAddressId);

        if (find) return find;
    }

    // 默认选择绑定了店铺的发货地址
    if (shopId) {
        const find = shippingAddresses.find((it) => {
            if (it.bindingShopIds && it.bindingShopIds.split(",").map(Number).indexOf(shopId) !== -1) return true;
        });

        if (find) return find;
    }

    // 都没有则选择第一个地址
    if (shippingAddresses.length > 0) return shippingAddresses[0];

    return null;
}

/**
 * props:
 * - shopId 店铺ID，用于默认选择绑定了该店铺的发货地址
 * - waybillTemplate 快递单模板绑定了默认发货人设置
 * - defaultShippingAddressId 旧面单打印会传入一个地址
 * - onChange(ShippingAddress)
 *
 * methods:
 * - setShippingAddress(shippingAddressId) 可能选中发货单或者电子面单，会有默认的发货地址
 *
 * tips:
 * - 1、用户在设置-快递单模板-默认发货人的设置（WaybillTemplate.defaultShippingAddressId）
 * - 2、用户在设置-发货地址-绑定店铺中的设置（ShippingAddress.bindingShopIds）
 * - 3、如果上面规则都无法适用时，默认选中默认选中第一个发货地址
 */

interface ShippingAddressSelectProps {
    onChange?: (e: ShippingAddressDTO) => void;

    checked?: ShippingAddressDTO;

    value?: ShippingAddressDTO;

    shopId?: number;

    waybillTemplate?: waybillTemplateDTO;

    /** @todo 此属性的用法存在歧义，当前并非按照defaultValue的用法，而是一个value的用法 */
    defaultShippingAddressId?: number;

    onLoad?(e: ShippingAddressDTO[]): void;

    className?: string;

    style?: React.CSSProperties;
}

export interface ShippingAddressSelectInstance {
    refresh(): Promise<void>;
}

/**
 * 构建发货地址文本
 */
export function buildShippingAddressContent(shippingAddress: ShippingAddressDTO) {
    const parts: string[] = [];
    if (shippingAddress.name) {
        parts.push(shippingAddress.name, "-");
    }
    parts.push(
        shippingAddress.shipperName,
        "，",
        shippingAddress.province,
        shippingAddress.city,
        shippingAddress.district,
        shippingAddress.street,
        "，",
        shippingAddress.mobile,
        " ",
        shippingAddress.telephone,
    );
    return parts.join("");
}

const ShippingAddressSelect: React.ForwardRefRenderFunction<
    ShippingAddressSelectInstance,
    ShippingAddressSelectProps
> = (props, ref) => {
    const { onChange, checked, shopId, waybillTemplate, defaultShippingAddressId, value, onLoad, className, style } =
        props;

    const globalStore = useGlobalStore();

    const shippingAddresses = globalStore.waybillPrintStore.shippingAddresses;
    const loading = globalStore.waybillPrintStore.shippingAddressesLoading;
    const refresh = globalStore.waybillPrintStore.loadShippingAddresses;

    useEffect(() => {
        globalStore.waybillPrintStore.ensureShippingAddresses();
    }, []);

    const handleChange = useCallback(
        (value) => {
            const selectedShippingAddress = shippingAddresses.find((it) => it.id === value);

            selectedShippingAddress && onChange?.(selectedShippingAddress);
        },
        [shippingAddresses, onChange],
    );

    // 设置默认发货地址
    useEffect(() => {
        if (shippingAddresses?.length) {
            const defaultShippingAddress = getDefaultShippingAddress(
                shippingAddresses,
                waybillTemplate,
                shopId,
                defaultShippingAddressId,
            );

            defaultShippingAddress && onChange?.(defaultShippingAddress);
        }
    }, [shippingAddresses, waybillTemplate, shopId, defaultShippingAddressId]);

    useEffect(() => {
        shippingAddresses && onLoad?.(shippingAddresses);
    }, [shippingAddresses]);

    useImperativeHandle(ref, () => ({ refresh }));

    return (
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <Select
                onChange={handleChange}
                value={checked?.id || value?.id}
                loading={loading}
                popupMatchSelectWidth={false}
                className={className}
                style={style}
                showSearch={{
                    optionFilterProp: "label",
                }}
                data-openreplay-masked
                options={[
                    ...(shippingAddresses?.map((it) => ({
                        key: it.id,
                        value: it.id,
                        "data-openreplay-masked": true,
                        label: buildShippingAddressContent(it),
                    })) ?? []),
                ]}
            />
            <RedoOutlined
                className="ml10 text-gray"
                spin={loading}
                disabled={loading}
                onClick={async () => {
                    await refresh();

                    message.success("刷新发货地址成功");
                }}
            />
        </div>
    );
};

export default observer(React.forwardRef(ShippingAddressSelect));
